/**
 * Helper pour gérer les notifications Web Push
 */

import apiClient from "../api/apiClient";

/**
 * Convertit une clé VAPID base64 en Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function subscriptionUsesApplicationServerKey(
  subscription: PushSubscription,
  expectedKey: Uint8Array
): boolean {
  const currentKey = subscription.options?.applicationServerKey;
  if (!currentKey) {
    return false;
  }

  const currentKeyBytes = new Uint8Array(currentKey);
  if (currentKeyBytes.length !== expectedKey.length) {
    return false;
  }

  return currentKeyBytes.every((byte, index) => byte === expectedKey[index]);
}

async function removeObsoletePushSubscription(
  subscription: PushSubscription
): Promise<void> {
  const endpoint = subscription.endpoint;
  const unsubscribed = await subscription.unsubscribe();

  if (!unsubscribed) {
    throw new Error("Impossible de supprimer l'ancien abonnement push");
  }

  try {
    await apiClient.post("/api/notifications/push/unsubscribe", { endpoint });
  } catch (error) {
    // Le backend desactivera aussi cet abonnement au prochain refus VAPID.
    console.warn(
      "[Web Push] Ancien abonnement supprime du navigateur, mais pas encore du serveur:",
      error
    );
  }
}

/**
 * Vérifie si les notifications push sont supportées
 */
export function isPushNotificationSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export interface PushSubscriptionOptions {
  requestPermission?: boolean;
}

/**
 * Demande la permission pour les notifications
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) {
    console.warn("[Web Push] Notifications non supportées");
    return "denied";
  }

  try {
    const permission = await Notification.requestPermission();

    return permission;
  } catch (error) {
    console.error("[Web Push] Erreur demande permission:", error);
    return "denied";
  }
}

/**
 * Enregistre le Service Worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    console.warn("[Web Push] Service Worker non supporté");
    return null;
  }

  try {
    // Vérifier si déjà enregistré
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) {
      return existing;
    }

    const registration = await navigator.serviceWorker.register("/service-worker.js", {
      scope: "/",
    });

    return registration;
  } catch (error) {
    console.error("[Web Push] Erreur enregistrement SW:", error);
    return null;
  }
}

/**
 * S'abonne aux notifications push
 */
export async function subscribeToPushNotifications(
  options: PushSubscriptionOptions = {}
): Promise<boolean> {
  if (!isPushNotificationSupported()) {
    console.warn("[Web Push] Push non supporté");
    return false;
  }

  try {
    // 1. Vérifier la permission actuelle AVANT de demander
    let permission = Notification.permission;

    // La demande native doit toujours venir d'une action explicite du user.
    // Les réparations automatiques ne sont autorisées que si la permission
    // avait déjà été accordée par le navigateur.
    if (permission === "default" && options.requestPermission === true) {
      permission = await requestNotificationPermission();
    }

    if (permission !== "granted") {
      return false;
    }

    // 2. Enregistrer le Service Worker

    let registration = await registerServiceWorker();
    if (!registration) {
      throw new Error("Impossible d'enregistrer le Service Worker");
    }

    // Attendre activation
    await navigator.serviceWorker.ready;

    // 3. Récupérer la clé publique VAPID depuis le serveur
    const { data } = await apiClient.get("/api/notifications/vapid-public-key");
    const vapidPublicKey = data.publicKey;

    if (!vapidPublicKey) {
      throw new Error("Clé VAPID publique non disponible");
    }

    // 4. Réutiliser uniquement un abonnement créé avec la clé VAPID active
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const existingSubscription =
      await registration.pushManager.getSubscription();
    if (existingSubscription) {
      if (
        subscriptionUsesApplicationServerKey(
          existingSubscription,
          applicationServerKey
        )
      ) {
        await apiClient.post("/api/notifications/push/subscribe", {
          subscription: existingSubscription.toJSON(),
        });
        return true;
      }

      // Une rotation VAPID invalide les anciens abonnements. Le navigateur doit
      // les supprimer avant d'en créer un avec la nouvelle clé publique.
      await removeObsoletePushSubscription(existingSubscription);
    }

    // 5. Créer un nouvel abonnement
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    // 6. Envoyer l'abonnement au serveur
    await apiClient.post("/api/notifications/push/subscribe", {
      subscription: subscription.toJSON(),
    });

    return true;
  } catch (error: any) {
    console.error("[Web Push] Erreur lors de l'abonnement:", error);
    return false;
  }
}

/**
 * Se désabonne des notifications push
 */
export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Désabonner localement
      await subscription.unsubscribe();

      // Informer le serveur
      await apiClient.post("/api/notifications/push/unsubscribe", {
        endpoint: subscription.endpoint,
      });

      return true;
    }

    return false;
  } catch (error) {
    console.error("[Web Push] Erreur lors du désabonnement:", error);
    return false;
  }
}

/**
 * Vérifie l'état de l'abonnement push
 */
export async function checkPushSubscriptionStatus(): Promise<{
  isSubscribed: boolean;
  permission: NotificationPermission;
}> {
  let isSubscribed = false;
  let permission: NotificationPermission = "default";

  if (!isPushNotificationSupported()) {
    return { isSubscribed, permission };
  }

  try {
    permission = Notification.permission;

    // 🔧 FIX: Vérifier d'abord si un SW est enregistré
    // navigator.serviceWorker.ready attend indéfiniment si aucun SW n'est enregistré
    const registrations = await navigator.serviceWorker.getRegistrations();

    if (registrations.length === 0) {
      // Aucun Service Worker enregistré → impossible d'être abonné

      return { isSubscribed: false, permission };
    }

    // Si un SW existe, attendre qu'il soit prêt
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    isSubscribed = subscription !== null;
  } catch (error) {
    console.error("[Web Push] Erreur vérification statut:", error);
  }

  return { isSubscribed, permission };
}

/**
 * Initialise les notifications push après le login
 * À appeler dans AuthContext ou après authentification réussie
 */
export async function initializePushNotifications(): Promise<void> {
  if (!isPushNotificationSupported()) {
    return;
  }

  try {
    const { permission } = await checkPushSubscriptionStatus();

    // Ne jamais provoquer de demande native au login. Si le user a déjà
    // autorisé les notifications, on peut en revanche réparer ou resynchroniser
    // l'abonnement sans nouvelle interaction.
    if (permission !== "granted") {
      return;
    }

    // Réenregistrer un abonnement existant permet aussi de réactiver côté
    // serveur un endpoint toujours valide localement. La même opération répare
    // un abonnement absent ou créé avec une ancienne clé VAPID.
    await subscribeToPushNotifications({ requestPermission: false });
  } catch (error) {
    console.error("💥 [Web Push] Erreur initialisation:", error);
  }
}
